import { ApolloError } from '@apollo/client';
import { I18n } from '@lingui/core';
import { isProject } from '../constants/projectTarget';
import { t } from '@lingui/core/macro';
import { ErrorCodeEnum } from '@mildang/graphql-artifact';

// TODO use
export enum ErrorCode {
  BAD_REQUEST = 'BAD_REQUEST',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
  NOT_FOUND = 'NOT_FOUND',
  UNAUTHENTICATED = 'UNAUTHENTICATED',
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  GOOGLE_NLP_NOT_SUPPORT_LANGUAGE = 'GOOGLE_NLP_NOT_SUPPORT_LANGUAGE',
  MONTHLY_REPORT_BATCH_NAME_DUPLICATED = 'MONTHLY_REPORT_BATCH_NAME_DUPLICATED',
  TEXT_SENTENCE_DO_NOT_MATCH = 'TEXT_SENTENCE_DO_NOT_MATCH',
  AT_LEAST_ONE_SENTENCE_MUST_EXIST = 'AT_LEAST_ONE_SENTENCE_MUST_EXIST',
  NO_SENTENCE = 'NO_SENTENCE',
  CHANGE_ANSWER_NOT_ALLOWED = 'CHANGE_ANSWER_NOT_ALLOWED',
  TEXTBOOK_DELETED_CAN_NOT_PROCESSED = 'TEXTBOOK_DELETED_CAN_NOT_PROCESSED',
  NOT_FOUND_USABLE_STUDY_ACTIVITY = 'NOT_FOUND_USABLE_STUDY_ACTIVITY',
  SENTENCE_DO_NOT_MATCH = 'SENTENCE_DO_NOT_MATCH',
  NOT_FOUND_GROUP_USER = 'NOT_FOUND_GROUP_USER',
  GLOBAL_MODUM_NAME_DUPLICATED = 'GLOBAL_MODUM_NAME_DUPLICATED',
  CREATE_SA_ON_DELETED_META_CHAPTER_NOT_ALLOWED = 'CREATE_SA_ON_DELETED_META_CHAPTER_NOT_ALLOWED',
  META_CHAPTERS_WITH_META_CONTENT_MODULES_ARE_NOT_ALLOWED_TO_HAVE_CHILD_META_CHAPTERS = 'META_CHAPTERS_WITH_META_CONTENT_MODULES_ARE_NOT_ALLOWED_TO_HAVE_CHILD_META_CHAPTERS',
  PERMISSION_DENIED = 'PERMISSION_DENIED',
  GROUP_NOT_HAVE_GLOBAL_MODUM = 'GROUP_NOT_HAVE_GLOBAL_MODUM',
  INVALID_PASSWORD = 'INVALID_PASSWORD',
  DELETED = 'DELETED',
  AI_FEEDBACK_NOT_NEEDED = 'AI_FEEDBACK_NOT_NEEDED',
  AI_FEEDBACK_NOT_READY_STUDY_UNIT_NOT_DONE = 'AI_FEEDBACK_NOT_READY_STUDY_UNIT_NOT_DONE',
  AI_FEEDBACK_REQUEST_FAILURE = 'AI_FEEDBACK_REQUEST_FAILURE',
  AIDT_TEACHER_NOT_SETUP = 'AIDT_TEACHER_NOT_SETUP', // AIDT에서 학생이 선생님 수업 데이터 초기화 대기 상태 표시
  CONTAINS_FORBIDDEN_WORD_ERROR = 'CONTAINS_FORBIDDEN_WORD_ERROR',
  MPD_CHANNEL_NOT_FOUND = 'MPD_CHANNEL_NOT_FOUND',
  MATH_AI_TUTOR_CHANNEL_NOT_FOUND = 'MATH_AI_TUTOR_CHANNEL_NOT_FOUND',
  REFERENCE_INFORMATION_NOT_FOUND = 'REFERENCE_INFORMATION_NOT_FOUND',
  REFERENCE_INFORMATION_EMPTY = 'REFERENCE_INFORMATION_EMPTY',
  REFERENCE_INFORMATION_CHANNEL_NOT_FOUND = 'REFERENCE_INFORMATION_CHANNEL_NOT_FOUND',
  REFERENCE_INFORMATION_TOO_LARGE = 'REFERENCE_INFORMATION_TOO_LARGE',
}

export enum LoginErrorCode {
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
  BAD_USER_INPUT = 'BAD_USER_INPUT',
}

const BR = '\n';

const getErrorMessages = (i18n: I18n) => ({
  [ErrorCode.UNKNOWN_ERROR]: t(
    i18n,
  )`잠시 서버에 문제가 있거나, 일시적인 오류일 수 있습니다.${BR}잠시 후 다시 확인해주세요.`,
  [ErrorCode.NOT_FOUND]: t(i18n)`데이터를 찾을 수 없습니다.`,
  [ErrorCode.UNAUTHENTICATED]: t(i18n)`로그인이 필요한 요청입니다.`,
  [ErrorCode.FORBIDDEN]: t(i18n)`요청할 수 있는 권한이 없습니다.`,
  [ErrorCode.GOOGLE_NLP_NOT_SUPPORT_LANGUAGE]: t(
    i18n,
  )`지원하지 않는 언어가 포함되어 있습니다.\n영어 혹은 한글을 사용해 지문을 수정해주세요.`,
  [ErrorCode.MONTHLY_REPORT_BATCH_NAME_DUPLICATED]: t(
    i18n,
  )`이미 있는 리포트 묶음 이름입니다. 묶음 이름을 수정해주세요.`,
  [ErrorCode.CHANGE_ANSWER_NOT_ALLOWED]: t(i18n)`자, 결과보단 과정을 멋지게 수행해볼까요?`,
  [ErrorCode.TEXTBOOK_DELETED_CAN_NOT_PROCESSED]: t(i18n)`삭제되거나 사라진 콘텐츠입니다`,
  ['ALREADY_EXISTING_META_CONTENT_MODULE_ON_META_CHATTER']: t(i18n)`이미 등록된 단어가 포함되어 있습니다.`,
  [ErrorCode.NOT_FOUND_GROUP_USER]: t(i18n)`데이터를 찾을 수 없습니다.`,
  [ErrorCode.GLOBAL_MODUM_NAME_DUPLICATED]: t(i18n)`이미 사용중인 이름입니다.`,
  [ErrorCode.GROUP_NOT_HAVE_GLOBAL_MODUM]: t(
    i18n,
  )`메뉴 -> 수업관리 -> 모둠설정 에서 모둠을 먼저 세팅해주세요`,
  [ErrorCode.PERMISSION_DENIED]: t(i18n)`사용하시는 계정에서는 이 요청에 대한 권한이 없습니다.`,
  [ErrorCode.INVALID_PASSWORD]: t(i18n)`비밀번호가 일치하지 않습니다.`,
  [ErrorCodeEnum.QUESTION_BLOCK_MUST_BE_UNDER_NUMBER_BLOCK]: t(
    i18n,
  )`질문 블록이 문항 번호 블록 안에 있어야 발행할 수 있습니다.`,
  [ErrorCodeEnum.CHECK_BLOCK_POSITION_AND_RETRY]: t(i18n)`블록 위치를 확인하고 다시 발행해 주세요.`,
  [ErrorCodeEnum.INVALID_SCHEMA]: t(i18n)`시스템 오류로 인해 발행할 수 없습니다.`,
  [ErrorCode.CONTAINS_FORBIDDEN_WORD_ERROR]: t(i18n)`금지어가 포함되어 있습니다.`,
});

const errorDescriptions = (i18n: I18n) => {
  const deleteTarget = isProject('schoolpt') ? t(i18n)` 수업이` : t(i18n)`교재가`;
  return {
    [ErrorCode.TEXTBOOK_DELETED_CAN_NOT_PROCESSED]: t(
      i18n,
    )`해당 ${deleteTarget} 삭제되어 북마크를 할 수 없어요.`,
    [ErrorCode.NOT_FOUND_USABLE_STUDY_ACTIVITY]: t(i18n)`커리큘럼내 가져올 스터디액티비티가 없습니다.`,
  };
};

const loginErrorMessages = (i18n: I18n) => ({
  [LoginErrorCode.UNKNOWN_ERROR]: t(
    i18n,
  )`잠시 서버에 문제가 있거나, 일시적인 오류일 수 있습니다.${BR}잠시 후 다시 확인해주세요.`,
  [LoginErrorCode.BAD_USER_INPUT]: t(i18n)`잘못된 이메일 또는 비밀번호입니다.`,
  [ErrorCode.INVALID_PASSWORD]: t(i18n)`비밀번호가 일치하지 않습니다.`,
});

export function getApolloErrorParserCode(error?: ApolloError) {
  const errorCode: ErrorCode = (error?.graphQLErrors?.[0]?.extensions?.code ||
    ErrorCode.UNKNOWN_ERROR) as ErrorCode;

  return errorCode;
}

export function apolloErrorParser(i18n: I18n, error?: ApolloError) {
  const errorCode = getApolloErrorParserCode(error);

  return {
    title:
      getErrorMessages(i18n)[errorCode] ||
      error?.graphQLErrors?.[0]?.message ||
      getErrorMessages(i18n)[ErrorCode.UNKNOWN_ERROR],
    desc: errorDescriptions(i18n)[errorCode],
  };
}

export function apolloLoginErrorParser(i18n: I18n, error?: ApolloError): string {
  const errorCode = getApolloErrorParserCode(error);

  return (
    loginErrorMessages(i18n)[errorCode] ||
    error?.graphQLErrors?.[0]?.message ||
    getErrorMessages(i18n)[ErrorCode.UNKNOWN_ERROR]
  );
}

export function apolloSignUpErrorParser(error?: ApolloError): string {
  return (
    // @ts-ignore
    error?.graphQLErrors?.[0]?.extensions?.response?.message?.[0] || errorMessages[ErrorCode.UNKNOWN_ERROR]
  );
}

export function updateSentenceErrorParser(i18n: I18n, error?: ApolloError): string {
  const errorCode = getApolloErrorParserCode(error);

  switch (errorCode) {
    case 'SENTENCE_DO_NOT_MATCH':
      return t(i18n)`이전 문장과 신규 문장이 일치하지 않습니다.`;
    case 'AT_LEAST_ONE_SENTENCE_MUST_EXIST':
      return t(i18n)`문장이 최소 1개 존재해야 합니다.`;
    case 'NO_SENTENCE':
      return t(i18n)`문장이 없습니다.`;
    default:
      return t(i18n)`알 수 없는 에러로 인해 요청이 실패했습니다.`;
  }
}
